// The page lifecycle: what happens when a page's content arrives, and what
// happens when it leaves.
//
// The site has one document per visit once the router is in (docs/prd/
// 2026-10-31-router-page-changes.md): the header, the footer scripts and the
// fade persist, and only `.content-wrapper` is replaced on a navigation. So
// everything a page starts must be something the page can stop:
//
//   mountPage(root)   mounts the shared features that any page may carry
//                     (Sprig's htmx, carousels, players, forms, charts,
//                     marquees, FAQ accordions, motion panels, the custom
//                     cursor, the UTM fields) and then the page's own features, chosen by
//                     the page id; marks the container `data-mounted`
//   unmountPage()     stops all of it: every listener added on `window`,
//                     `document` or `htmx` went on the page's AbortController
//                     signal, and every feature that holds a player, an
//                     animation, an observer or a timer gave back a teardown
//
// The contract: after unmountPage() the container is discarded. Features do
// not have to restore the markup they changed, only release what lives
// outside it. Listeners on elements inside the container go with the
// container and need no bookkeeping.
//
// Each feature is a function of the page context, `{ root, page, signal }`,
// and may return a teardown. Shared features run on every page and find
// their own markup (or nothing to do); page features run by id.
import $ from "../jquery-setup.js";
import { mountWhenPresent, mountWhenNear } from "./mount.js";
import { initCharts } from "./charts.js";
import { mountMarquees } from "../features/marquee.js";
import { mountFaq } from "../features/faq.js";
import { mountMotionPanels } from "../features/motion.js";
import { mountCursor } from "../features/cursor.js";
import { mountListing } from "../features/listing.js";
import { mountArticle } from "../features/article.js";
import { mountServicesSearch } from "../features/services.js";
import { mountSprig } from "../features/htmx.js";
import { mountEdge } from "../features/edge.js";

const SHARED = [
  mountSprig, // first: the listings below bind to the components it processes
  mountMarquees,
  mountFaq,
  mountMotionPanels,
  ({ root, page }) => mountWhenPresent(root, ".carousel", () => import("../features/carousel.js"), (m) => m.mount(root, page)),
  ({ root }) => mountWhenPresent(root, ".vimeo-controls, .video-pair", () => import("../features/video.js"), (m) => m.mount(root)),
  ({ root }) => mountWhenNear(root, ".growthcurve-form", () => import("../features/form.js"), (m) => m.mount(root)),
  ({ root }) => initCharts(root),
  (context) => mountCursor(context, ".content-card__img-holder", ".content-card__tag-panel"),
  // UTM parameters from the URL into cookies and the forms' hidden fields (site/init.js).
  () => $.fn.setUtm(),
  // The edge strip takes the colour of the page at the foot (iPhone Safari's toolbar band).
  mountEdge,
];

// Page features, by the id each template declares.
const PAGES = {
  services: mountServicesSearch,
  workIndex: (context) => mountListing(context, { item: ".work-filter__project-card" }),
  resultsIndex: (context) => mountListing(context, { item: ".work-filter__project-card", vimeoByWidth: true }),
  blogIndex: (context) => mountListing(context, { item: ".content-card__card-panel", search: true }),
  blog: mountArticle,
};

let current = null;

export function mountPage(root) {
  if (current) unmountPage();
  const controller = new AbortController();
  const context = { root, page: pageIdOf(root), signal: controller.signal };
  const teardowns = [];
  for (const feature of [...SHARED, PAGES[context.page]].filter(Boolean)) {
    const teardown = feature(context);
    if (typeof teardown === "function") teardowns.push(teardown);
  }
  root.dataset.mounted = "";
  current = { root, controller, teardowns };
  return current;
}

export function unmountPage() {
  if (!current) return;
  const { root, controller, teardowns } = current;
  current = null;
  controller.abort();
  // Last mounted, first stood down.
  for (const teardown of teardowns.reverse()) teardown();
  delete root.dataset.mounted;
}

export const currentPage = () => current;

// The page id: `data-page` on the container (the router's templates) or the
// `#pageId` marker the templates have carried until now.
function pageIdOf(root) {
  return root.dataset.page || document.getElementById("pageId")?.dataset.pageId || "";
}
